import { z } from 'zod';

/**
 * SIL-2543: inline reseller margin figures on /assets and marketplace listing
 * rows. POST /api/client/margin-intel/rows (read only; POST to carry up to
 * 100+100 ids in a body).
 *
 * v1 prices an asset row (its SKU) and a `sku`-type listing only. Lot and
 * asset-type listings come back `unavailable` (lot contents are verified-only
 * and no buyer accessor returns member SKU ids; a rollup needs its own
 * database + security review).
 */
export const MARGIN_INTEL_MAX_IDS = 100;
export const MARGIN_INTEL_DEFAULT_TARGET_MARGIN_PCT = 0.2;

const idList = z.array(z.string().uuid()).max(MARGIN_INTEL_MAX_IDS);

export const marginIntelRowsRequestSchema = z
  .object({
    assetIds: idList.default([]),
    listingIds: idList.default([]),
    targetMarginPct: z.number().finite().min(0).max(1).default(MARGIN_INTEL_DEFAULT_TARGET_MARGIN_PCT),
  })
  .refine((v) => v.assetIds.length + v.listingIds.length > 0, {
    message: 'at least one of assetIds / listingIds must be non-empty',
  });
export type MarginIntelRowsRequest = z.infer<typeof marginIntelRowsRequestSchema>;

/** One undifferentiated `unavailable` state: no existence or reason oracle. */
export const marginIntelSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('ok'),
    breakEvenBidUsd: z.number().nullable(),
    bidAtTargetMarginUsd: z.number().nullable(),
    bestMonth: z.number().int(),
    confidenceGrade: z.enum(['A', 'B', 'C', 'D']).nullable(),
    forecastAvailable: z.boolean(),
  }),
  z.object({ status: z.literal('unavailable') }),
]);
export type MarginIntel = z.infer<typeof marginIntelSchema>;

export const marginIntelRowsResponseSchema = z.object({
  assets: z.record(z.string(), marginIntelSchema),
  listings: z.record(z.string(), marginIntelSchema),
  /** false => figures carry no house costs; the UI links to cost settings. */
  costsConfigured: z.boolean(),
  stamp: z.object({
    formulaId: z.string(),
    forecastRunId: z.string().nullable(),
    forecastAsOf: z.string().nullable(),
    targetMarginPct: z.number(),
  }),
});
export type MarginIntelRowsResponse = z.infer<typeof marginIntelRowsResponseSchema>;
